import StoreState, { createAppAsyncThunk } from 'store/state'
import { planAdapter, scheduleAdapter } from './adapter'
import { AddTimeParams, api } from './net'
import { planToProfilesSelector, plansSelector } from './selectors'

import { ScheduleTime } from 'interfaces/Plan'
import { EVENT_SETUP_PLAN } from 'routes/trial/interfaces'
import { firstValueFrom } from 'rxjs'
import { connectedDestinationsSelector, getDestinations } from 'services/destinations'
import { HS_EVENT_PLAN_ACTION, trackHubspotEvent } from 'services/tracking/hubspot'
import { trackTrainingEvent } from 'services/users/tracking'

export const getPlan = createAppAsyncThunk(
  'plan/get',
  async ({ id, force }: { id: string, force?: boolean }, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as StoreState
    const storedPlans = plansSelector(state)
    const planIds = Object.keys(planToProfilesSelector(state))

    if (!force && storedPlans[id]) {
      return { data: storedPlans[id], planId: id }
    }

    try {
      const response = await firstValueFrom(dispatch(api.getPlan(id)))
      if (response.error) {
        return rejectWithValue(response.error)
      }

      const state = getState() as StoreState
      const connectedProfiles = connectedDestinationsSelector(state)
      const connectedProfilesPpids = Object.values(connectedProfiles).reduce((acc, profile) => {
        if (profile.ppid) {
          acc[profile.ppid] = true
        }
        return acc
      }, {} as Record<string, boolean>)
      const planIndex = planIds.indexOf(response.plan.id.toString())
      return {
        data: planAdapter({
          ...response.plan,
          name: response.plan.name || `Plan ${planIndex + 1}`,
          ppPageIds: (response.ppPageIds || []).map(id => id.toString()).filter((ppid: string) => connectedProfilesPpids[ppid])
        }),
        planId: response.plan.id?.toString() || id
      }
    } catch (error) {
      console.log('[getPlan()]', error)
      return rejectWithValue(error)
    }
  }
)

export const createPlan = createAppAsyncThunk(
  'plan/create',
  async (params: { ppids: string[], name: string, copyFromPlanId?: string }, { dispatch, rejectWithValue }) => {
    const { ppids, name, copyFromPlanId } = params
    try {
      const response = await firstValueFrom(dispatch(api.createPlan(ppids, name, copyFromPlanId)))
      if (response.error) {
        return rejectWithValue(response.error)
      }
      dispatch(trackTrainingEvent({ eventCode: EVENT_SETUP_PLAN }))
      dispatch(trackHubspotEvent(HS_EVENT_PLAN_ACTION, { type: 'created' }))
      return { id: response.planId.toString(), ppids, name }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const deletePlan = createAppAsyncThunk(
  'plan/delete',
  async (ppid: string, { dispatch }) => {
    await firstValueFrom(dispatch(api.deletePlan(ppid)))
    return ppid
  }
)

export const initPlans = createAppAsyncThunk(
  'plan/init',
  async (force: boolean = false, { dispatch, getState }) => {
    const [plans, tzResponse] = await Promise.all([
      firstValueFrom(dispatch(api.getPostingPlans())),
      force ? firstValueFrom(dispatch(api.getTimezones())) : Promise.resolve(null),
      dispatch(getDestinations(force))
    ])

    const state = getState() as StoreState
    const connectedProfiles = connectedDestinationsSelector(state)
    const connectedProfilesPpids = Object.values(connectedProfiles).reduce((acc, profile) => {
      if (profile.ppid) {
        acc[profile.ppid] = true
      }
      return acc
    }, {} as Record<string, boolean>)

    const plansMap: Record<string, { name: string, ppids: string[], isPaused: boolean, isDefault: boolean }> = {}
    let index = 0

    Object.keys(plans).forEach((id: string) => {
      const plan = plans[id]
      const ppPageIds = (plan.ppPageIds || []).map((id: any) => id.toString())
      const defaultName = `Plan ${index += 1}`
      const ppids = ppPageIds.filter((ppid: string) => connectedProfilesPpids[ppid])
      if (ppids.length > 0) { // filter out plans without profiles
        plansMap[id] = {
          ...plan,
          id,
          ppids,
          ppPageIds,
          name: plan.name || defaultName,
          order: typeof plan.order === 'number' ? plan.order : index
        }
      }
    })

    const data: { plans: Record<string, any>, userTimezone?: string, timezones?: Record<string, string> } = { plans: plansMap }
    if (tzResponse) {
      data.userTimezone = tzResponse.userTimezone
      data.timezones = tzResponse.timezones.reduce((zonesObject: { [key: string]: string }, current: any) => {
        zonesObject[current.zone] = current.label
        return zonesObject
      }, {})
    }
    return data
  }
)

export const addSchedule = createAppAsyncThunk(
  'plan/addSchedule',
  async (params: { planId: string, ppid: string, scheduleId: string }, { dispatch, rejectWithValue }) => {
    const response = await firstValueFrom(dispatch(api.addSchedule(params.planId, params.scheduleId)))
    if (response.error) {
      return rejectWithValue(response.error)
    }
    dispatch(trackTrainingEvent({ eventCode: EVENT_SETUP_PLAN }))
    return {
      schedule: scheduleAdapter(response.plan.tabs[0]),
      ppid: params.ppid
    }
  }
)

export const deleteSchedule = createAppAsyncThunk(
  'plan/deleteSchedule',
  async (params: { ppid: string, scheduleId: string }, { dispatch }) => {
    await firstValueFrom(dispatch(api.deleteSchedule(params.scheduleId)))
    return { scheduleId: params.scheduleId, ppid: params.ppid }
  }
)

export const addTime = createAppAsyncThunk(
  'plan/addTime',
  async (params: AddTimeParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await firstValueFrom(dispatch(api.addTime(params)))
      if (response.error) {
        return rejectWithValue(response.error)
      }

      const ids = typeof response.ids === 'number'
        ? [response.ids.toString()]
        : response.ids.map((id: number) => id.toString())

      dispatch(trackHubspotEvent(HS_EVENT_PLAN_ACTION, { type: 'edited' }))
      dispatch(trackTrainingEvent({ eventCode: EVENT_SETUP_PLAN }))
      return {
        planId: params.planId,
        scheduleId: params.scheduleId || response.tabId.toString(),
        hour: params.hour,
        minute: params.minute,
        timeOfDay: params.timeOfDay,
        ids,
        range: params.range,
        bucketId: params.bucketId,
        dayOfWeek: params.scheduleDay
      }
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const deleteTime = createAppAsyncThunk(
  'plan/deleteTime',
  async (
    { planId, scheduleId, timeslot }: { planId: string, scheduleId: string, timeslot: ScheduleTime },
    { dispatch, rejectWithValue }
  ) => {
    const response = await firstValueFrom(dispatch(api.deleteTime(timeslot.ids)))
    if (response.error) {
      return rejectWithValue(response.error)
    }
    dispatch(trackHubspotEvent(HS_EVENT_PLAN_ACTION, { type: 'edited' }))
    dispatch(trackTrainingEvent({ eventCode: EVENT_SETUP_PLAN }))
    return { planId, scheduleId, timeslot }
  }
)

type UpdateTimeParams = {
  planId: string,
  scheduleId: string,
  timeKey: string,
  ids: string[],
  hour: string,
  minute: string,
  timeOfDay: string,
  bucketId?: string
  range?: boolean
}

export const updateTime = createAppAsyncThunk(
  'plan/updateTime',
  async (params: UpdateTimeParams, { dispatch, rejectWithValue }) => {
    const { planId, scheduleId, timeKey, ids, hour, minute, timeOfDay, bucketId, range } = params
    const response = await firstValueFrom(dispatch(api.updateTime(ids, hour, minute, timeOfDay, bucketId, range)))
    if (response.error) {
      return rejectWithValue(response.error)
    }
    dispatch(trackHubspotEvent(HS_EVENT_PLAN_ACTION, { type: 'edited' }))
    dispatch(trackTrainingEvent({ eventCode: EVENT_SETUP_PLAN }))
    return { planId, scheduleId, ids, hour, minute, timeOfDay, timeKey, bucketId, range }
  }
)

export const updatePlanTimezone = createAppAsyncThunk(
  'plan/updateTimezone',
  async ({ planId, timezone }: { planId: string, timezone: string }, { dispatch }) => {
    await firstValueFrom(dispatch(api.updateTimezone(planId, timezone)))
    return { planId, timezone }
  }
)

export const togglePlanPaused = createAppAsyncThunk(
  'plan/togglePaused',
  async (planId: string, { dispatch }) => {
    await firstValueFrom(dispatch(api.togglePlanPaused(planId)))
    return { planId }
  }
)

export const addProfilesToPlan = createAppAsyncThunk(
  'plan/updateProfiles',
  async ({ planId, ppids }: { planId: string, ppids: string[] }, { dispatch }) => {
    await firstValueFrom(dispatch(api.addProfilesToPlan(planId, ppids)))
    return { planId, ppids }
  }
)

export const updatePlan = createAppAsyncThunk(
  'plan/update',
  async (
    { planId, name, addPpids, copyFrom }: { planId: string, name: string, addPpids?: string[], copyFrom?: string },
    { dispatch, rejectWithValue }) => {
    try {
      await firstValueFrom(dispatch(api.updatePlan(planId, name, addPpids, copyFrom)))
      return { planId, name, addedPpids: addPpids }
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const reorderPlans = createAppAsyncThunk(
  'plan/reorder',
  async (planIds: string[], { dispatch, rejectWithValue }) => {
    try {
      await firstValueFrom(dispatch(api.reorderPlans(planIds)))
      return planIds
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)
